import { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { useScrollReveal } from "../hooks/useAnimations";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

export default function Contact({ t }) {
  const [ref, vis] = useScrollReveal();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const emailSubject = subject || `Contact from ${name || "Website"}`;
  const body = `${message}\n\n---\nFrom: ${name}\nEmail: ${email}`;

  const openGmail = (e) => {
    e.preventDefault();
    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(t.contact.emailValue)}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, "_blank", "noopener,noreferrer");
  };

  const openDefaultEmail = (e) => {
    e.preventDefault();
    window.location.href = `mailto:${t.contact.emailValue}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-white">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 reveal ${vis ? "visible" : ""}`}>
        <div className="text-center mb-14">
          <div className="inline-flex items-center justify-center rounded-full bg-brand-blue px-4 py-1.5 mb-5">
            <span className="text-white text-xs font-semibold tracking-widest uppercase">
              {t.contact.sectionLabel}
            </span>
          </div>
          <h2
            data-testid="contact-title"
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-brand-blue leading-tight tracking-tight mb-4"
          >
            {t.contact.title}
          </h2>
          <p className="text-slate-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-4 space-y-4">
            <div
              data-testid="contact-address"
              className="rounded-2xl border border-brand-blue/20 bg-card-mint p-6 shadow-sm hover:shadow-md hover:border-brand-blue/50 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center mb-4 border-2 border-brand-blue">
                <MapPin className="w-5 h-5 text-brand-blue" />
              </div>
              <p className="text-label-muted text-xs font-medium uppercase tracking-wider mb-1">
                {t.contact.address}
              </p>
              <p className="text-[#333333] text-base font-medium">
                {t.contact.addressValue}
              </p>
            </div>

            <div
              data-testid="contact-email"
              className="rounded-2xl border border-brand-blue/20 bg-card-mint p-6 shadow-sm hover:shadow-md hover:border-brand-blue/50 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center mb-4 border-2 border-brand-blue">
                <Mail className="w-5 h-5 text-brand-blue" />
              </div>
              <p className="text-label-muted text-xs font-medium uppercase tracking-wider mb-1">
                {t.contact.email}
              </p>
              <a
                href={`mailto:${t.contact.emailValue}`}
                className="text-[#333333] text-base font-medium hover:text-brand-blue transition-colors"
              >
                {t.contact.emailValue}
              </a>
            </div>

            <div
              data-testid="contact-phone"
              className="rounded-2xl border border-brand-blue/20 bg-card-mint p-6 shadow-sm hover:shadow-md hover:border-brand-blue/50 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center mb-4 border-2 border-brand-blue">
                <Phone className="w-5 h-5 text-brand-blue" />
              </div>
              <p className="text-label-muted text-xs font-medium uppercase tracking-wider mb-1">
                {t.contact.phone}
              </p>
              <a
                href={`tel:${t.contact.phoneValue.replace(/\s/g, "")}`}
                className="text-[#333333] text-base font-medium hover:text-brand-blue transition-colors"
              >
                {t.contact.phoneValue}
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 flex">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 shadow-md flex flex-col gap-5 w-full min-h-full"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="sr-only">
                    {t.contact.formName}
                  </label>
                  <Input
                    id="contact-name"
                    type="text"
                    placeholder={t.contact.formName}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 rounded-xl border-brand-blue/25 bg-card-mint/50 focus:bg-white focus:border-brand-blue"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email-input" className="sr-only">
                    {t.contact.formEmail}
                  </label>
                  <Input
                    id="contact-email-input"
                    type="email"
                    placeholder={t.contact.formEmail}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl border-brand-blue/25 bg-card-mint/50 focus:bg-white focus:border-brand-blue"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="sr-only">
                  {t.contact.formSubject}
                </label>
                <Input
                  id="contact-subject"
                  type="text"
                  placeholder={t.contact.formSubject}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-12 rounded-xl border-brand-blue/25 bg-card-mint/50 focus:bg-white focus:border-brand-blue"
                />
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                <label htmlFor="contact-message" className="sr-only">
                  {t.contact.formMessage}
                </label>
                <Textarea
                  id="contact-message"
                  placeholder={t.contact.formMessage}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={7}
                  className="rounded-xl border-brand-blue/25 bg-card-mint/50 focus:bg-white focus:border-brand-blue resize-none flex-1 min-h-[160px]"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={openGmail}
                  className="h-12 px-6 rounded-xl bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold gap-2"
                >
                  <Send className="w-4 h-4" />
                  {t.contact.openGmail}
                </Button>
                <Button
                  type="button"
                  onClick={openDefaultEmail}
                  variant="outline"
                  className="h-12 px-6 rounded-xl border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {t.contact.openDefault}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
